import { ReactNode, useState } from "react";
import { Link2 } from "lucide-react";
import { UUID } from "@bitory-gmbh/nidavellir";
import { toast } from "sonner";
import t from "@/lang/lang";
import { Dialog, DialogContent } from "@/shared/components/ui/dialog";
import {
  CommonDialogFooter,
  CommonDialogHeader,
  CommonDialogTrigger,
} from "@/shared/components/dialog/dialog-common";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  useHtzReportShowQuery,
  useHtzReportUpdateMicrobialAssessmentSampleMutation,
} from "@/services/backend/htz/report/api/report-api";
import { useSamplesAirMicrobialSampleSetListQuery } from "@/services/backend/samples/api/air-microbial-api";
import { useSamplesSurfaceMicrobialSampleSetListQuery } from "@/services/backend/samples/api/surface-microbial-api";
import { useSamplesDustDensitySampleListQuery } from "@/services/backend/samples/api/dust-density-sample-api";
import { useSamplesWaterSampleListQuery } from "@/services/backend/samples/api/water-sample-api";
import { Kind } from "@/services/backend/samples/types/kind";
import {
  Field,
  FieldDescription,
  FieldLabel,
} from "@/shared/components/ui/field";
import { InputDebounced } from "@/shared/components/inputs/input-debounced";
import { Button } from "@/shared/components/ui/button";
import { cn } from "@/shared/lib/utils";
import {
  joinFieldName,
  useErrValidationFailedContext,
} from "@/shared/lib/errors/err-validation-failed-context";

/**
 * ReportMicrobialSampleLinkDialog links an existing sample to a microbial
 * assessment of the report.
 *
 * A sample keeps the document it was taken for - linking it here does not
 * move it. This is what makes a sample taken once usable in several
 * documents, e.g. an Außenluftprobe.
 */
export function ReportMicrobialSampleLinkDialog({
  reportId,
  assessmentId,
  trigger,
  open: controlledOpen,
  onOpenChange,
  onBeforeLink,
  onLinked,
  disabled,
}: {
  reportId: UUID;
  assessmentId: UUID;
  trigger?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  // onBeforeLink runs before the sample is linked, for a caller whose
  // assessment does not exist yet. Creating it here rather than before
  // the dialog opens keeps a dismissed dialog from leaving a blank
  // assessment behind. Rejecting it aborts the link.
  onBeforeLink?: () => Promise<unknown>;
  onLinked?: () => void;
  disabled?: boolean;
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  const setOpen = onOpenChange ?? setUncontrolledOpen;

  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<SampleOption | null>(null);
  const [link, { isLoading, error, reset }] =
    useHtzReportUpdateMicrobialAssessmentSampleMutation();
  const { resetField } = useErrValidationFailedContext();

  const options = useSampleOptions(reportId, search);

  const onConfirm = () => {
    if (isLoading || !selected) {
      return;
    }

    Promise.resolve(onBeforeLink?.())
      .then(() =>
        link({
          id: reportId,
          assessmentId,
          sampleKind: selected.kind,
          sampleId: selected.id,
        }).unwrap(),
      )
      .then(() => {
        toast.success(t("Probe verknüpft"));
        const fieldName = joinFieldName("microbialAssessments", assessmentId);
        resetField(joinFieldName(fieldName, "sampleId"));
        resetField(joinFieldName(fieldName, "kind"));
        setSelected(null);
        setSearch("");
        reset();
        setOpen(false);
        onLinked?.();
      })
      .catch(() => {
        // error is surfaced via RTKQueryErrorAlert; swallow the rejection
      });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!isControlled &&
        (trigger || (
          <CommonDialogTrigger
            icon={Link2}
            label={t("Probe verknüpfen")}
            variant="outline"
            size="sm"
            disabled={disabled}
          />
        ))}
      <DialogContent>
        <CommonDialogHeader
          title={t("Bestehende Probe verknüpfen")}
          description={t(
            "Die Probe wird der Beurteilung zugeordnet. Sie bleibt dem Dokument zugeordnet, für das sie genommen wurde.",
          )}
        />
        <Field>
          <FieldLabel>{t("Suche")}</FieldLabel>
          <InputDebounced
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("Probennummer, Anlage oder Ort")}
          />
          <FieldDescription>
            {t("Es werden bis zu 20 Proben angezeigt.")}
          </FieldDescription>
        </Field>
        <div className="max-h-64 space-y-1 overflow-auto">
          {options.length === 0 && (
            <p className="text-muted-foreground text-sm">
              {t("Keine Proben gefunden.")}
            </p>
          )}
          {options.map((option) => (
            <Button
              key={option.id}
              type="button"
              variant="ghost"
              className={cn(
                "h-auto w-full justify-start rounded-sm px-2 py-1.5 text-left font-normal",
                selected?.id === option.id && "bg-accent",
              )}
              onClick={() => setSelected(option)}
            >
              <span className="grid">
                <span className="font-medium">{option.number}</span>
                <span className="text-muted-foreground text-xs">
                  {[kindLabel(option.kind), option.location, option.spot]
                    .filter((part) => part !== "")
                    .join(" · ")}
                </span>
              </span>
            </Button>
          ))}
        </div>
        <RTKQueryErrorAlert error={error} />
        <CommonDialogFooter
          isLoading={isLoading}
          onClickAction={onConfirm}
          actionLabel={t("Verknüpfen")}
          disabled={selected === null}
        />
      </DialogContent>
    </Dialog>
  );
}

interface SampleOption {
  id: string;
  kind: Kind;
  number: string;
  location: string;
  spot: string;
}

/**
 * useSampleOptions collects the samples of every kind and filters them by
 * the search term. The kinds are listed separately by the backend, so they
 * are merged here and cut to a readable number of results.
 *
 * Only samples of the report's company are offered: a sample is taken for
 * a company, and the backend requires every list to be scoped by one.
 */
function useSampleOptions(reportId: UUID, search: string): SampleOption[] {
  const { data: report } = useHtzReportShowQuery({ id: reportId });
  const companyId = report?.companyId ?? "";
  const request = { company: { active: true, values: [companyId] } };
  const queryOptions = { skip: companyId === "" };

  const { data: air } = useSamplesAirMicrobialSampleSetListQuery(
    request,
    queryOptions,
  );
  const { data: surface } = useSamplesSurfaceMicrobialSampleSetListQuery(
    request,
    queryOptions,
  );
  const { data: dust } = useSamplesDustDensitySampleListQuery(
    request,
    queryOptions,
  );
  const { data: water } = useSamplesWaterSampleListQuery(request, queryOptions);

  const options: SampleOption[] = [
    ...(air?.sampleSets ?? []).map((s) => toOption(s, Kind.AirMicrobial)),
    ...(surface?.sampleSets ?? []).map((s) =>
      toOption(s, Kind.SurfaceMicrobial),
    ),
    ...(dust?.samples ?? []).map((s) => toOption(s, Kind.DustDensity)),
    ...(water?.samples ?? []).map((s) => toOption(s, Kind.Water)),
  ];

  const term = search.trim().toLowerCase();
  const matching =
    term === ""
      ? options
      : options.filter((option) =>
          [option.number, option.location, option.spot]
            .join(" ")
            .toLowerCase()
            .includes(term),
        );

  return matching.slice(0, 20);
}

function toOption(
  sample: { id: string; number: string; location: string; spot: string },
  kind: Kind,
): SampleOption {
  return {
    id: sample.id,
    kind,
    number: sample.number,
    location: sample.location,
    spot: sample.spot,
  };
}

function kindLabel(kind: Kind | ""): string {
  switch (kind) {
    case Kind.AirMicrobial:
      return t("Luftkeimprobe");
    case Kind.SurfaceMicrobial:
      return t("Oberflächenkeimprobe");
    case Kind.DustDensity:
      return t("Staubdichtemessung");
    case Kind.Water:
      return t("Wasserprobe");
    default:
      return t("Keine Probe");
  }
}
